import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { MessageFeedbackEntry } from '@/types/aiChat';

/**
 * Redux slice for managing per-message feedback (thumbs up/down + reasons)
 * for the Gigawatt AI chat.
 *
 * This is the single source of truth for feedback state — it is populated
 * once on history restore and updated optimistically on user interaction,
 * rather than relying on fields riding along on the CopilotKit message
 * object (which may be stripped during CopilotKit's own normalization).
 *
 * `idMap` tracks temp (streaming) message id -> persisted message id, so a
 * message just-streamed-in can be voted on before the persisted id has
 * propagated anywhere else. Deliberately NOT applied by mutating CopilotKit's
 * own messages array (via setMessages) — doing so was found to trigger
 * CopilotKit's internal "message changed" handling (which attempts to
 * regenerate/continue the run and crashes on a browser-incompatible
 * `process.env` reference). Consumers resolve the effective id to use via
 * `idMap[messageId] ?? messageId` instead.
 *
 * `resolvedIds` tracks which (already-resolved) message ids the backend has
 * confirmed a persisted id for — either loaded directly from history restore,
 * or via a successful setIdMapping. A message whose id isn't in here has no
 * confirmed persisted id yet (streaming still in flight, or the mapping was
 * skipped due to a cardinality mismatch), so feedback must stay disabled for
 * it rather than submit against an id the backend may never have persisted.
 *
 * State structure:
 * {
 *   feedback: { [messageId]: { rating, reasons } },
 *   idMap: { [tempId]: persistedId },
 *   resolvedIds: { [resolvedId]: true },
 * }
 */

export type MessageFeedbackState = {
  feedback: Record<string, MessageFeedbackEntry>;
  idMap: Record<string, string>;
  resolvedIds: Record<string, true>;
};

const initialState: MessageFeedbackState = {
  feedback: {},
  idMap: {},
  resolvedIds: {},
};

const messageFeedbackSlice = createSlice({
  name: 'messageFeedback',
  initialState,
  reducers: {
    // Set feedback for a single message (used for optimistic updates and rollback)
    setFeedback: (
      state,
      action: PayloadAction<{ messageId: string } & MessageFeedbackEntry>
    ) => {
      const { messageId, rating, reasons } = action.payload;
      state.feedback[messageId] = { rating, reasons };
    },

    // Merge feedback for multiple messages at once (used for history restore)
    setFeedbackBulk: (
      state,
      action: PayloadAction<Record<string, MessageFeedbackEntry>>
    ) => {
      Object.assign(state.feedback, action.payload);
    },

    // Merge temp id -> persisted id mappings (used once a streamed message's
    // id is confirmed persisted). The persisted ids (values) are also marked
    // resolved, since a successful mapping confirms the backend has them.
    setIdMapping: (state, action: PayloadAction<Record<string, string>>) => {
      Object.assign(state.idMap, action.payload);
      Object.values(action.payload).forEach(persistedId => {
        state.resolvedIds[persistedId] = true;
      });
    },

    // Mark message ids as having a confirmed persisted id (used for history
    // restore, where every loaded message's id is already backend-persisted).
    markIdsResolved: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach(id => {
        state.resolvedIds[id] = true;
      });
    },

    // Clear all feedback and id mappings (used when starting a new chat)
    clearAllFeedback: () => initialState,
  },
});

export const {
  setFeedback,
  setFeedbackBulk,
  setIdMapping,
  markIdsResolved,
  clearAllFeedback,
} = messageFeedbackSlice.actions;

export default messageFeedbackSlice;
